import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { PersonFormValues } from '../api/types';
import { SectionHeading } from '../components/SectionHeading';
import { StatusBanner } from '../components/StatusBanner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { defaultPersonFormValues, toPersonFormValues, toPersonPayload, validatePersonForm } from '../features/v1/forms';

type PersonFormPageProps = {
  mode: 'create' | 'edit';
};

const fieldClass =
  'mt-2 flex min-h-[52px] w-full rounded-[2px] border border-input bg-card px-4 py-3 text-base shadow-none transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

export const PersonFormPage = ({ mode }: PersonFormPageProps) => {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [values, setValues] = useState<PersonFormValues>({
    ...defaultPersonFormValues,
    targetType: searchParams.get('targetType') === 'pet' ? 'pet' : 'person',
  });
  const [loading, setLoading] = useState(mode === 'edit');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'edit') return;
    const run = async () => {
      try {
        const person = await api.getPersonById(id);
        setValues(toPersonFormValues(person));
      } catch (error) {
        setError(error instanceof Error ? error.message : '대상 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [id, mode]);

  const setField =
    <K extends keyof PersonFormValues>(key: K) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setValues({ ...values, [key]: event.target.value } as PersonFormValues);
    };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validatePersonForm(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = toPersonPayload(values);
      const saved = mode === 'create' ? await api.createPerson(payload) : await api.updatePerson(id, payload);
      navigate(`/people/${saved.id}`);
    } catch (error) {
      setError(error instanceof Error ? error.message : '저장하지 못했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <SectionHeading
        eyebrow={mode === 'create' ? 'New Subject' : 'Edit Subject'}
        title={mode === 'create' ? '사람/반려동물 추가' : '대상 정보 수정'}
        description="이름, 관계, 태그를 입력하고 중요 날짜는 상세 화면에서 추가합니다."
      />
      {error ? <StatusBanner title="폼 작업에 실패했습니다" tone="danger" description={error} /> : null}
      {loading ? (
        <Card className="p-6 text-sm font-semibold text-muted-foreground">불러오는 중...</Card>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>기본 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label>대상 유형</Label>
                <select className={fieldClass} value={values.targetType} onChange={setField('targetType')}>
                  <option value="person">사람</option>
                  <option value="pet">반려동물</option>
                </select>
              </div>
              <div>
                <Label>이름</Label>
                <Input value={values.name} onChange={setField('name')} placeholder="예: 엄마, 초코" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>관계</Label>
                  <Input value={values.relation} onChange={setField('relation')} placeholder="가족" />
                </div>
                <div>
                  <Label>분류</Label>
                  <Input value={values.category} onChange={setField('category')} placeholder="parent, dog" />
                </div>
              </div>
              <div>
                <Label>메모</Label>
                <textarea className={`${fieldClass} min-h-32`} value={values.memo} onChange={setField('memo')} placeholder="챙길 내용" />
              </div>
              <div>
                <Label>태그</Label>
                <Input value={values.tags} onChange={setField('tags')} placeholder="예: 가족, 중요" />
              </div>
            </CardContent>
          </Card>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? '저장 중...' : mode === 'create' ? '대상 추가' : '변경 저장'}
          </Button>
        </form>
      )}
    </div>
  );
};
